import React from 'react';
import { TestResult } from '../types';
import { useTheme } from '../contexts/ThemeContext';

interface ResultsDisplayProps {
  result: TestResult;
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ result }) => {
  const { mode } = useTheme();
  const isLight = mode === 'light';
  
  const {
    speed,
    error,
    time,
    unit,
    bytesReceived,
    durationLimited
  } = result;

  // Simplified color logic based on speed value in Mbps
  const getSpeedColorClass = (): string => {
    if (error) return isLight ? 'text-red-600' : 'text-red-500';
    if (speed === null) return isLight ? 'text-gray-900' : 'text-green-500';

    let speedInMbps = speed;
    if (unit === 'Kbps') speedInMbps = speed / 1000;
    if (unit === 'Gbps') speedInMbps = speed * 1000;

    if (speedInMbps < 5) return isLight ? 'text-orange-500' : 'text-orange-400';
    if (speedInMbps >= 30) return isLight ? 'text-gray-700' : 'text-green-400';
    return isLight ? 'text-gray-600' : 'text-green-500';
  };

  return (
    <div className={`
      w-full border p-4 md:p-6
      ${isLight 
        ? 'bg-white border-gray-200 shadow-sm' 
        : 'bg-gray-900 border-green-500/30'
      }
    `}>
      <h3 className={`
        text-lg font-mono mb-3 md:mb-4 text-center
        ${isLight ? 'text-gray-900' : 'text-green-500'}
      `}>
        Test Results
        {time && (
          <sup className={`
            text-xs ml-1 font-normal
            ${durationLimited 
              ? (isLight ? 'text-orange-500' : 'text-orange-400')
              : (isLight ? 'text-gray-500' : 'text-green-400')
            }
          `}>
            {durationLimited ? 'stop' : ''} {(time / 1000).toFixed(1)}s
          </sup>
        )}
      </h3>
      
      {error ? (
        <div className={`
          p-3 md:p-4 border font-mono text-center
          ${isLight 
            ? 'bg-red-50 border-red-200 text-red-600' 
            : 'bg-red-900/20 border-red-500/30 text-red-400'
          }
        `}>
          <p className="font-medium mb-1">Error:</p>
          <p>{error}</p>
        </div>
      ) : (
        <div className="text-center font-mono">
          <div className="mb-3 md:mb-4">
            <p className={`
              text-sm mb-1
              ${isLight ? 'text-gray-600' : 'text-green-400'}
            `}>
              Average Download Speed
            </p>
            <div className={`text-3xl md:text-4xl font-bold ${getSpeedColorClass()} ${!isLight && 'glow'}`}>
              {speed !== null ? speed : '--'} 
              <span className="text-lg md:text-xl ml-1">{speed !== null ? unit : ''}</span>
            </div>
          </div>
          
          <div className={`
            grid grid-cols-2 gap-3 md:gap-4 text-sm border-t pt-3 md:pt-4
            ${isLight 
              ? 'text-gray-500 border-gray-200' 
              : 'text-green-400/80 border-green-500/20'
            }
          `}>
            <div>
              <p className="mb-1">Downloaded</p>
              <p className={isLight ? 'text-gray-900' : 'text-green-500'}>
                {bytesReceived !== undefined ? `${(bytesReceived / (1024*1024)).toFixed(1)} MB` : 'N/A'}
              </p>
            </div>
            <div>
              <p className="mb-1">Time</p>
              <p className={isLight ? 'text-gray-900' : 'text-green-500'}>
                {time !== null ? `${(time / 1000).toFixed(2)} sec` : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsDisplay; 